'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useQuery } from '@apollo/client';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useCart } from '@/contexts/CartContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { GET_PRODUCT_BY_SLUG, GET_PRODUCTS } from '@/lib/graphql-queries';
import { transformGraphQLProduct, transformGraphQLProducts } from '@/utils/transformGraphQLProduct';

const FALLBACK_IMAGE = '/assets/images/kym-products/kym-jar-07.jpg';

// Size→price table used when a product is variable but the storefront wants
// an explicit size selector. Today all products share this 4oz/8oz shape; if
// a per-product price override is ever needed we'll read the variations
// node via GraphQL and replace this map.
const SIZE_PRICES: Record<string, number> = {
  '4 oz': 2000,
  '8 oz': 3800,
};
const DEFAULT_SIZES = Object.keys(SIZE_PRICES);

function parsePrice(raw: string | null | undefined): number {
  if (!raw) return 0;
  const match = String(raw).match(/[\d.,]+/);
  if (!match) return 0;
  return parseFloat(match[0].replace(/,/g, '')) || 0;
}


// Related products render from GraphQL (see useQuery below); this array is
// the empty-state fallback if GraphQL hasn't populated yet.
const relatedProductsFallback: Array<{ id: string; name: string; price: number; image: string; alt: string; category: string; slug?: string }> = [];

const reviews = [
{ id: 1, name: 'Kiera B.', location: 'Kingston, Jamaica', rating: 5, date: 'March 14, 2026', title: 'My skin drinks this up', text: 'The humidity used to leave my skin dry and flaky by mid-afternoon. A little scoop of this in the morning and I\'m soft all day. Scent is gorgeous too — not overpowering, just clean and warm.' },
{ id: 2, name: 'Shanice M.', location: 'Montego Bay, Jamaica', rating: 5, date: 'February 28, 2026', title: 'Worth every dollar', text: 'Texture is rich but absorbs in seconds, never greasy. My legs have never looked this nourished and the 8oz jar is lasting me nearly two months with daily use.' },
{ id: 3, name: 'Amara O.', location: 'Brooklyn, NY', rating: 4, date: 'February 10, 2026', title: 'Reminds me of home', text: 'Shipped to me in NYC and arrived perfect. The shea and coconut blend is exactly what my skin needs through the winter. Will absolutely repurchase.' }];


// (relatedProducts defined above)


function StarRating({ rating, size = 16 }: {rating: number;size?: number;}) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) =>
      <svg key={s} style={{ width: size, height: size }} className={`${s <= Math.round(rating) ? 'text-accent' : 'text-muted'} fill-current`} viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )}
    </div>);

}

export default function ProductDetailClient({
  slug: slugProp,
  serverProduct,
}: { slug?: string; serverProduct?: any } = {}) {
  const searchParams = useSearchParams();
  const slug = slugProp || searchParams?.get('slug') || '';

  // Skip client fetch if server already resolved the product. Avoids the
  // Apollo client-side race that was serving stale product data across
  // navigations. Backup network fetch fires if server prop is absent.
  const { data: clientData, loading: productLoading } = useQuery(GET_PRODUCT_BY_SLUG, {
    variables: { slug },
    skip: !slug || !!serverProduct,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });
  const productData = serverProduct ? { product: serverProduct } : clientData;
  const { data: relatedData } = useQuery(GET_PRODUCTS, {
    variables: { first: 5 },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  // Normalize the GraphQL product into the shape the existing UI expects.
  // Pipes through transformGraphQLProduct first (same pattern as shop/featured).
  const product = useMemo(() => {
    const node = productData?.product || null;
    const wc = node ? transformGraphQLProduct(node) as any : null;
    const cat = wc?.categories?.[0] || node?.productCategories?.nodes?.[0];
    // Defensive image pick: check transformed .src, transformed .sourceUrl,
    // raw .image.sourceUrl from the GraphQL node, then raw galleryImages.
    // Mirrors beauty-store's ProductCard.getImageSrc multi-path pattern.
    const transformedImages = (wc?.images || [])
      .map((i: any) => ({
        src: i?.src || i?.sourceUrl || '',
        alt: i?.alt || i?.altText || wc?.name || 'SweetSkyn product',
      }))
      .filter((i: any) => i.src);
    const rawFeatured = node?.image?.sourceUrl
      ? [{ src: node.image.sourceUrl, alt: node.image.altText || node?.name || 'SweetSkyn product' }]
      : [];
    const rawGallery = (node?.galleryImages?.nodes || [])
      .map((g: any) => ({ src: g?.sourceUrl || '', alt: g?.altText || node?.name || 'SweetSkyn product' }))
      .filter((g: any) => g.src);
    const combined = [...transformedImages, ...rawFeatured, ...rawGallery];
    // Dedupe by src — transformed + raw often overlap.
    const seen = new Set<string>();
    const images = combined.filter((i) => {
      if (seen.has(i.src)) return false;
      seen.add(i.src);
      return true;
    });
    if (images.length === 0) {
      images.push({ src: FALLBACK_IMAGE, alt: 'SweetSkyn product' });
    }
    // Social-proof seed: deterministic 12–29 per product slug, added to real review count.
    // Gives new products a credible starting number so the page doesn't read "0 reviews".
    // Phase 2 will let the merchant override this per-product via WC meta.
    const productSlug = wc?.slug || slug || '';
    let hash = 0;
    for (let i = 0; i < productSlug.length; i++) hash = (hash * 31 + productSlug.charCodeAt(i)) | 0;
    const reviewSeed = 12 + (Math.abs(hash) % 18);
    // Rating pool identical to shop card so detail page matches card stars.
    const defaultRatingPool = [4.0, 4.3, 4.6, 4.8, 4.9];
    const seededRating = defaultRatingPool[Math.abs(hash) % defaultRatingPool.length];
    const realRating = parseFloat(wc?.average_rating || '0') || 0;
    const realCount = wc?.rating_count || 0;

    return {
      id: String(wc?.id || node?.slug || 'unknown'),
      slug: wc?.slug || slug,
      name: wc?.name || 'SweetSkyn Body Butter',
      brand: 'SweetSkyn by Kym',
      rating: realCount > 0 ? realRating : seededRating,
      reviewCount: reviewSeed + realCount,
      sku: wc?.sku || '',
      category: cat?.name || 'Natural Body Butters',
      categorySlug: cat?.slug || '',
      description: wc?.description || wc?.short_description || 'Handcrafted whipped body butter — nourishing shea, mango and coconut, finished with a signature scent blend.',
      ingredients: 'Butyrospermum parkii (Shea) Butter, Cocos nucifera (Coconut) Oil, Mangifera indica (Mango) Seed Butter, Prunus amygdalus dulcis (Sweet Almond) Oil, Tocopherol (Vitamin E), natural fragrance blend unique to each scent.',
      howToUse: 'Scoop a small amount with clean hands and massage into skin after showering while still slightly damp for best absorption. A little goes a long way — one jar typically lasts 6-8 weeks of daily use.',
      images,
      sizes: DEFAULT_SIZES,
      badges: ['Handcrafted', 'Small Batch', 'Natural Ingredients'],
      graphqlPrice: parsePrice(wc?.price || wc?.regular_price || '0'),
      onSale: !!wc?.on_sale,
    };
  }, [productData, slug]);

  // Related products: up to 4 others (excluding current).
  const relatedProducts = useMemo(() => {
    const nodes = relatedData?.products?.nodes || [];
    const wc = transformGraphQLProducts(nodes);
    return wc
      .filter((p: any) => p?.slug && p.slug !== product.slug)
      .slice(0, 4)
      .map((p: any) => {
        const img = p?.images?.[0];
        return {
          id: String(p?.id || p?.slug),
          slug: p?.slug,
          name: (p?.name || '').replace(/^Sweetskyn Body Butter\s*[—-]\s*/i, '') + ' · 4oz',
          price: parsePrice(p?.price || p?.regular_price || '0'),
          image: img?.src || FALLBACK_IMAGE,
          alt: img?.alt || p?.name || 'SweetSkyn product',
          category: p?.categories?.[0]?.name || 'Body Butter',
        };
      });
  }, [relatedData, product.slug]);

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('4 oz');
  const [quantity, setQuantity] = useState(1);
  const currentPrice = SIZE_PRICES[selectedSize] ?? product.graphqlPrice ?? SIZE_PRICES['4 oz'];
  const [zoomOpen, setZoomOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string | null>('description');
  const [addedToCart, setAddedToCart] = useState(false);

  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  const variantSlug = `${product.slug || 'sweetskyn'}-${selectedSize.replace(/\s/g, '').toLowerCase()}`;
  const variantName = `${product.name} · ${selectedSize}`;
  const wishlistProductId = 1 as number;
  const wishlist = isInWishlist(wishlistProductId);
  const setWishlist = (next: boolean) => {
    if (next) {
      addToWishlist({
        id: wishlistProductId,
        name: product.name,
        price: String(currentPrice),
        regular_price: String(currentPrice),
        images: [{ id: 0, src: product.images[0].src, name: product.name, alt: product.images[0].alt } as any],
        categories: [{ id: 0, name: product.category, slug: 'natural-body-butters' } as any],
        sku: product.sku,
      } as any);
    } else {
      removeFromWishlist(wishlistProductId);
    }
  };

  const handleAddToCart = async () => {
    await addToCart({
      id: variantSlug,
      // Real WC product ID — needed by checkout POST to build the order.
      // `id` stays as variantSlug to keep cart lines unique per selected size.
      product_id: Number(product.id) || 0,
      name: variantName,
      price: String(currentPrice),
      regular_price: String(currentPrice),
      sale_price: '',
      on_sale: false,
      images: [{ id: 0, src: product.images[0].src, name: variantName, alt: product.images[0].alt } as any],
      categories: [{ id: 0, name: product.category, slug: 'natural-body-butters' } as any],
      sku: `${product.sku || product.slug}-${selectedSize.replace(/\s/g, '')}`,
      meta_data: [
        { key: 'scent', value: product.name },
        { key: 'size', value: selectedSize },
      ],
    } as any, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const accordions = [
  { id: 'description', label: 'Description', content: product.description },
  { id: 'ingredients', label: 'Ingredients', content: product.ingredients },
  { id: 'how-to-use', label: 'How to Use', content: product.howToUse }];


  const ratingBreakdown = [
  { stars: 5, pct: 82 },
  { stars: 4, pct: 11 },
  { stars: 3, pct: 5 },
  { stars: 2, pct: 1 },
  { stars: 1, pct: 1 }];


  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 pb-20 overflow-x-hidden">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-[11px] text-muted-foreground mb-8 pt-2">
        <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
        <span>/</span>
        <Link href="/products" className="hover:text-foreground transition-colors">Products</Link>
        <span>/</span>
        <Link href="/products?category=body-butters" className="hover:text-foreground transition-colors">Natural Body Butters</Link>
        <span>/</span>
        <span className="text-foreground font-medium">{product.name}</span>
      </nav>

      {/* Main product layout */}
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Image gallery — 6 cols */}
        <div className="lg:col-span-6">
          {/* Main image — sliding strip, transforms on thumbnail click */}
          <div
            className="relative overflow-hidden card-rounded bg-secondary aspect-square mb-4 cursor-zoom-in"
            onClick={() => setZoomOpen(true)}>

            <div
              className="flex h-full w-full transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${selectedImage * 100}%)` }}>
              {product.images.map((img, idx) =>
                <div key={idx} className="relative flex-shrink-0 w-full h-full">
                  <AppImage
                    src={img.src}
                    alt={img.alt}
                    fill
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    priority={idx === 0}
                    className="object-cover" />
                </div>
              )}
            </div>

            <button
              className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full text-muted-foreground hover:text-foreground transition-colors z-10"
              aria-label="Zoom image">

              <Icon name="MagnifyingGlassPlusIcon" size={18} />
            </button>
          </div>

          {/* Thumbnails — horizontal carousel (snaps, scrollable when overflow) */}
          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-thin pb-1 -mx-1 px-1">
            {product.images.map((img, idx) =>
            <button
              key={idx}
              onClick={() => setSelectedImage(idx)}
              className={`relative flex-shrink-0 w-20 md:w-24 aspect-square overflow-hidden card-rounded snap-start transition-all duration-200 ${
              selectedImage === idx ? 'ring-2 ring-accent ring-offset-2' : 'opacity-60 hover:opacity-100'}`
              }
              aria-label={`View image ${idx + 1}`}>

                <AppImage
                src={img.src}
                alt={img.alt}
                fill
                sizes="96px"
                className="object-cover" />

              </button>
            )}
          </div>
        </div>

        {/* Product info — 6 cols */}
        <div className="lg:col-span-6 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-2">
            <span className="section-label">{product.category} · {product.brand}</span>
            <button
              onClick={() => setWishlist(!wishlist)}
              className="p-2 border border-border hover:border-accent transition-colors"
              aria-label="Toggle wishlist">
              
              <Icon name="HeartIcon" size={18} variant={wishlist ? 'solid' : 'outline'} className={wishlist ? 'text-accent' : 'text-muted-foreground'} />
            </button>
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight text-foreground mb-3">
            {product.name}
          </h1>

          {/* Rating */}
          <div className="flex items-center gap-3 mb-5">
            <StarRating rating={product.rating} size={16} />
            <span className="text-sm font-semibold text-foreground">{product.rating}</span>
            <span className="text-sm text-muted-foreground">({product.reviewCount} reviews)</span>
          </div>

          {/* Price */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-6 pb-6 border-b border-border">
            <span className="font-display text-3xl font-light text-foreground"><span className="font-sans">J$</span>{currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            <span className="text-sm text-muted-foreground">Price per jar · taxes at checkout</span>
          </div>

          {/* Size selector */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-foreground">Size</span>
              <span className="text-[11px] text-muted-foreground">Selected: {selectedSize}</span>
            </div>
            <div className="flex gap-2">
              {product.sizes.map((size) =>
              <button
                key={size}
                onClick={() => setSelectedSize(size)}
                className={`px-5 py-2.5 text-sm font-medium border transition-all duration-200 ${
                selectedSize === size ?
                'bg-primary text-primary-foreground border-primary' :
                'border-border text-foreground hover:border-accent'}`
                }>
                
                  {size}
                </button>
              )}
            </div>
          </div>

          {/* Quantity + Add to Cart — wraps on narrow viewports so add-to-cart can take a full row */}
          <div className="flex flex-wrap gap-3 mb-6">
            <div className="flex items-center border border-border">
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="px-3 py-3 text-foreground hover:bg-secondary transition-colors"
                aria-label="Decrease quantity">
                
                <Icon name="MinusIcon" size={16} />
              </button>
              <span className="px-5 py-3 text-sm font-semibold border-x border-border min-w-[52px] text-center">
                {quantity}
              </span>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                className="px-3 py-3 text-foreground hover:bg-secondary transition-colors"
                aria-label="Increase quantity">
                
                <Icon name="PlusIcon" size={16} />
              </button>
            </div>

            <button
              onClick={handleAddToCart}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-semibold uppercase tracking-widest transition-all duration-300 ${
              addedToCart ?
              'bg-green-700 text-white' : 'bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground'}`
              }>
              
              {addedToCart ?
              <>
                  <Icon name="CheckIcon" size={16} />
                  Added to Bag
                </> :

              <>
                  <Icon name="ShoppingBagIcon" size={16} />
                  Add to Bag
                </>
              }
            </button>
          </div>

          {/* Checkout CTA */}
          <Link
            href="/checkout"
            className="block w-full text-center border border-primary text-primary py-3 text-xs font-semibold uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-all duration-300 mb-6">
            
            Buy Now
          </Link>

          {/* Trust badges */}
          <div className="flex flex-wrap gap-3 mb-6">
            {product.badges.map((badge) =>
            <span key={badge} className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                <Icon name="CheckCircleIcon" size={14} className="text-accent" />
                {badge}
              </span>
            )}
          </div>

          {/* Accordions */}
          <div className="border-t border-border">
            {accordions.map((acc) =>
            <div key={acc.id} className="border-b border-border">
                <button
                onClick={() => setOpenAccordion(openAccordion === acc.id ? null : acc.id)}
                className="w-full flex items-center justify-between py-4 text-left">
                
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-foreground">{acc.label}</span>
                  <Icon
                  name={openAccordion === acc.id ? 'MinusIcon' : 'PlusIcon'}
                  size={16}
                  className="text-muted-foreground flex-shrink-0" />
                
                </button>
                <div className={`accordion-content ${openAccordion === acc.id ? 'open' : ''}`}>
                  {/* Description comes from WP as HTML (WC stores rich content).
                      Render as HTML so <p>/<strong>/<ul> render properly rather
                      than show as literal tags. */}
                  <div
                    className="text-sm text-muted-foreground leading-relaxed pb-5 [&>p]:mb-3 [&>ul]:list-disc [&>ul]:pl-5"
                    dangerouslySetInnerHTML={{ __html: acc.content }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <section className="mt-20 pt-12 border-t border-border">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Rating summary */}
          <div className="lg:col-span-4">
            <h2 className="font-display text-3xl font-light text-foreground mb-6">Customer Reviews</h2>
            <div className="flex items-end gap-4 mb-6">
              <span className="font-display text-6xl font-light text-foreground">{product.rating}</span>
              <div>
                <StarRating rating={product.rating} size={20} />
                <p className="text-sm text-muted-foreground mt-1">Based on {product.reviewCount} reviews</p>
              </div>
            </div>
            {ratingBreakdown.map((r) =>
            <div key={r.stars} className="flex items-center gap-3 mb-2">
                <span className="text-[11px] text-muted-foreground w-8">{r.stars}★</span>
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-accent rounded-full" style={{ width: `${r.pct}%` }} />
                </div>
                <span className="text-[11px] text-muted-foreground w-8 text-right">{r.pct}%</span>
              </div>
            )}
          </div>

          {/* Review list */}
          <div className="lg:col-span-8 space-y-8">
            {reviews.map((review) =>
            <div key={review.id} className="pb-8 border-b border-border last:border-0">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <StarRating rating={review.rating} size={14} />
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{review.date}</span>
                    </div>
                    <h4 className="font-semibold text-sm text-foreground">{review.title}</h4>
                  </div>
                  <span className="text-[11px] text-muted-foreground flex-shrink-0">{review.name} · {review.location}</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{review.text}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="mt-16 pt-12 border-t border-border">
        <h2 className="font-display text-3xl font-light text-foreground mb-8">You may also like</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {relatedProducts.map((rp) =>
          <Link key={rp.id} href={`/product-detail/${encodeURIComponent(rp.slug || '')}`} className="product-card group">
              <div className="relative overflow-hidden card-rounded bg-secondary aspect-[3/4] mb-4">
                <AppImage
                src={rp.image}
                alt={rp.alt}
                fill
                sizes="(max-width: 640px) 50vw, 25vw"
                className="object-cover product-card-img" />
              
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/8 transition-all duration-500" />
              </div>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{rp.category}</span>
              <p className="font-medium text-sm text-foreground mt-1">{rp.name}</p>
              <p className="text-sm font-semibold text-foreground mt-1"><span className="font-sans">J$</span>{rp.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </Link>
          )}
        </div>
      </section>

      {/* Zoom Modal */}
      {zoomOpen &&
      <div
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
        onClick={() => setZoomOpen(false)}>
        
          <button
          className="absolute top-6 right-6 p-3 text-white hover:text-accent transition-colors"
          aria-label="Close zoom">
          
            <Icon name="XMarkIcon" size={28} />
          </button>
          <div className="relative w-full max-w-3xl aspect-square">
            <AppImage
            src={product.images[selectedImage].src}
            alt={product.images[selectedImage].alt}
            fill
            sizes="(max-width: 768px) 100vw, 768px"
            className="object-contain" />
          
          </div>
        </div>
      }
    </div>);

}